/* istanbul ignore file */ // framework loading code is implicitly tested enough that we do not need explicit tests

/**
 * Returns a loader, a set of functions that based off the given config
 * for loading bundled assets in the browser
 *
 * Used by Bootloader.ts
 */
export function forConfig(config) {
	if (!config) {
		throw new Error("'config' must be set!");
	}
	if (!config.envMode) {
		throw new Error("'config.envMode' must be set!");
	}
	if (!config.loader) {
		throw new Error("'config.loader' must be set!");
	}

	/**
	 * Loads the given bundle into the browser by injecting a script tag into the page.
	 * @param {string} bundleUrl A URL to a specific bundle
	 */
	function loadBundle(bundleUrl) {
		console.log(`[rm3] Loading browser bundle '${bundleUrl}' in production mode.`);

		// SHOULDDO: Add load timeout of 15 seconds.
		return new Promise((resolve, reject) => {
			const script = document.createElement('script');
			script.type = 'text/javascript';
			script.async = true;
			// SHOULDDO: Wrap error and include more context instead of using error object from callback directly.
			script.onerror = reject;
			script.onload = function () {
				resolve(window._rm3_just_loaded_bundle_exports);
				delete window._rm3_just_loaded_bundle_exports;
			};
			script.src = bundleUrl;
			document.getElementsByTagName('head')[0].appendChild(script);
		});
	}

	const exports = {};

	/**
	 * Loads a "shared bundle" by id. A shared bundle is shared between components bundles and is loaded only once.
	 * Shared bundles are suffixed with `bundle`.
	 */
	exports.loadSharedBundle = async function (bundleId) {
		if (config.loader.bundleLoadMode === 'script') {
			// Used to load components from built bundles (typically in production)
			if (!config.loader.sharedBaseUrl) {
				throw new Error("'config.loader.sharedBaseUrl' must be set!");
			}
			const exports = await loadBundle(`${config.loader.sharedBaseUrl}/${bundleId}.bundle.js`);
			return exports.default || exports;
		}
		// When running in dev workspace, load vendors directly from source
		if (bundleId === 'vendor') {
			return;
		}
		// NOTE: We provide the module to be loaded via 'moduleId' so that webpack does not attempt to build chunks.
		const moduleId = `./${bundleId}-bundle.ts`;
		const exports = await import(moduleId);
		return exports.default || exports;
	};

	// A cache of component bundles
	const loadedComponentBundles = {};

	/**
	 * Loads a component bundle, component bundles can have a variety of types.
	 *
	 * @param {'script' | 'deps' | 'ui' | 'page'} type - The type of bundle. Type determines what will be loaded.
	 * @param {string} componentBundleId - The component bundle ID is the path to the bundle.
	 * @returns
	 */
	exports.loadComponentBundle = async function (type, componentBundleId) {
		const key = `${type}:${componentBundleId}`;
		if (!loadedComponentBundles[key]) {
			if (config.loader.bundleLoadMode === 'script') {
				// Used to load components from built bundles (typically in production)
				if (!config.loader.componentsBaseUrl) {
					throw new Error("'config.loader.componentsBaseUrl' must be set!");
				}
				loadedComponentBundles[key] = loadBundle(`${config.loader.componentsBaseUrl}/${componentBundleId}.${type}.bundle.js`);
			}
			// Only used to load components when running in UI development workspace
			else if (type === 'deps') {
				// NOTE: We provide the module to be loaded via 'moduleId' so that webpack does not attempt to build chunks.
				const moduleId = `../../components/${componentBundleId}_Bundle.tsx`;
				loadedComponentBundles[key] = import(moduleId);
			} else if (type === 'ui') {
				// NOTE: We provide the module to be loaded via 'moduleId' so that webpack does not attempt to build chunks.
				const moduleId = `../../components/${componentBundleId}_UI.tsx`;
				loadedComponentBundles[key] = import(moduleId);
			} else if (type === 'page') {
				// NOTE: We provide the module to be loaded via 'moduleId' so that webpack does not attempt to build chunks.
				const moduleId = `../../components/${componentBundleId}_Page.tsx`;
				loadedComponentBundles[key] = import(moduleId);
			} else {
				throw new Error(`Unknown bundle type '${type}'!`);
			}
		}
		return loadedComponentBundles[key];
	};

	return exports;
}
